import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import SecurityIcon from '@mui/icons-material/Security';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import RouteIcon from '@mui/icons-material/Route';
import {
    Box,
    Button,
    Card,
    CardContent,
    TextField,
    Typography,
    Paper,
    Stack,
    Alert,
    Grid,
    IconButton,
    Tooltip,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Chip
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useFormik } from 'formik';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import { httpClient } from '../../../common/httpclient';
import PageScaffold from '../../../component/pageScaffold/PageScaffold';
import Dropdown from '../../../component/dropdown/Dropdown';
import { standaloneFieldSx } from '../../../common/fieldStyles';

const HTTP_METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];

export default function PermissionForm() {
    const theme = useTheme();
    const navigate = useNavigate();
    const { id } = useParams();
    const [loading, setLoading] = useState(false);
    const [isLoadingInput, setIsLoadingInput] = useState(true);
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [message, setMessage] = useState('');
    const [severity, setSeverity] = useState('');
    const [pathAccesses, setPathAccesses] = useState([]);
    const [loadingPathAccesses, setLoadingPathAccesses] = useState(false);
    const [openPathAccessDialog, setOpenPathAccessDialog] = useState(false);
    const [editingPathAccess, setEditingPathAccess] = useState(null);
    const [pathAccessForm, setPathAccessForm] = useState({
        path: '',
        method: 'GET'
    });

    const validationSchema = Yup.object({
        name: Yup.string().required('Nome é obrigatório'),
        display_name: Yup.string().required('Nome de exibição é obrigatório'),
        group_permission: Yup.string().required('Grupo de permissão é obrigatório')
    });

    const pathAccessValidationSchema = Yup.object({
        path: Yup.string().required('Path é obrigatório'),
        method: Yup.string().required('Método é obrigatório')
    });

    const fetchPermission = async () => {
        if (id) {
            try {
                setLoading(true);
                const response = await httpClient.get(`/admin/permissions/${id}`);
                const permissionData = response.data;
                
                formik.setValues({
                    name: permissionData.name || '',
                    display_name: permissionData.display_name || '',
                    group_permission: permissionData.group_permission || ''
                });
            } catch (error) {
                console.error('Error fetching permission:', error);
                handleShowErrorSnackbar('Erro ao carregar permissão');
            } finally {
                setLoading(false);
                setIsLoadingInput(false);
            }
        } else {
            setIsLoadingInput(false);
        }
    };

    const fetchPathAccesses = async () => {
        if (id) {
            try {
                setLoadingPathAccesses(true);
                const response = await httpClient.get(`/admin/permissions/${id}/path-access`);
                const data = response.data;
                setPathAccesses(Array.isArray(data) ? data : []);
            } catch (error) {
                console.error('Error fetching path accesses:', error);
                handleShowErrorSnackbar('Erro ao carregar path accesses');
                setPathAccesses([]);
            } finally {
                setLoadingPathAccesses(false);
            }
        }
    };

    useEffect(() => {
        if (id) {
            fetchPermission();
            fetchPathAccesses();
        } else {
            setIsLoadingInput(false);
        }
    }, [id]);

    const formik = useFormik({
        initialValues: {
            name: '',
            display_name: '',
            group_permission: ''
        },
        validationSchema: validationSchema,
        enableReinitialize: true,
        onSubmit: async (values) => {
            try {
                setLoading(true);
                await httpClient.put(`/admin/permissions/${id}`, values);
                handleShowSuccessSnackbar();
                setTimeout(() => {
                    navigate('/admin/permissions');
                }, 2000);
            } catch (error) {
                console.error('Error updating permission:', error);
                const errorMessage = error.response?.data?.message || error.response?.data?.id || 'Erro ao atualizar permissão';
                handleShowErrorSnackbar(errorMessage);
            } finally {
                setLoading(false);
            }
        }
    });

    const handleShowSuccessSnackbar = () => {
        setOpenSnackbar(true);
        setMessage('Permissão atualizada com sucesso!');
        setSeverity('success');
    };

    const handleShowErrorSnackbar = (errorMessage) => {
        setOpenSnackbar(true);
        setMessage(errorMessage);
        setSeverity('error');
    };

    const handleOpenPathAccessDialog = (pathAccess = null) => {
        if (pathAccess) {
            setEditingPathAccess(pathAccess);
            setPathAccessForm({
                path: pathAccess.path || '',
                method: pathAccess.method || 'GET'
            });
        } else {
            setEditingPathAccess(null);
            setPathAccessForm({
                path: '',
                method: 'GET'
            });
        }
        setOpenPathAccessDialog(true);
    };

    const handleClosePathAccessDialog = () => {
        setOpenPathAccessDialog(false);
        setEditingPathAccess(null);
        setPathAccessForm({
            path: '',
            method: 'GET'
        });
    };

    const handleSavePathAccess = async () => {
        try {
            // Validação básica
            if (!pathAccessForm.path || !pathAccessForm.method) {
                handleShowErrorSnackbar('Path e método são obrigatórios');
                return;
            }

            setLoading(true);
            
            if (editingPathAccess) {
                // Para editar: criar novo path access e remover o antigo
                // 1. Criar novo path access
                const createResponse = await httpClient.post('/admin/path-access', {
                    path: pathAccessForm.path,
                    method: pathAccessForm.method
                });
                
                if (createResponse.status !== 200 && createResponse.status !== 201) {
                    throw new Error(createResponse.data?.message || 'Erro ao criar path access');
                }
                
                const newPathAccessId = createResponse.data.id;
                
                // 2. Adicionar novo e remover antigo
                await httpClient.put(`/admin/permissions/${id}/path-access`, {
                    add_path_access_ids: [newPathAccessId],
                    remove_path_access_ids: [editingPathAccess.id]
                });
                
                handleShowSuccessSnackbar();
            } else {
                // Criar novo path access
                // 1. Primeiro criar o path access
                const createResponse = await httpClient.post('/admin/path-access', {
                    path: pathAccessForm.path,
                    method: pathAccessForm.method
                });
                
                if (createResponse.status !== 200 && createResponse.status !== 201) {
                    throw new Error(createResponse.data?.message || 'Erro ao criar path access');
                }
                
                const newPathAccessId = createResponse.data.id;
                
                // 2. Adicionar à permissão
                await httpClient.put(`/admin/permissions/${id}/path-access`, {
                    add_path_access_ids: [newPathAccessId],
                    remove_path_access_ids: []
                });
                
                handleShowSuccessSnackbar();
            }
            
            handleClosePathAccessDialog();
            fetchPathAccesses();
        } catch (error) {
            console.error('Error saving path access:', error);
            const errorMessage = error.response?.data?.message || error.response?.data?.id || error.message || 'Erro ao salvar path access';
            handleShowErrorSnackbar(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const handleDeletePathAccess = async (pathAccessId) => {
        if (!window.confirm('Tem certeza que deseja remover este path access da permissão?')) {
            return;
        }

        try {
            setLoading(true);
            await httpClient.put(`/admin/permissions/${id}/path-access`, {
                add_path_access_ids: [],
                remove_path_access_ids: [pathAccessId]
            });
            handleShowSuccessSnackbar();
            fetchPathAccesses();
        } catch (error) {
            console.error('Error deleting path access:', error);
            const errorMessage = error.response?.data?.message || error.response?.data?.id || 'Erro ao remover path access';
            handleShowErrorSnackbar(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    if (isLoadingInput) {
        return (
            <PageScaffold links={[]} titlePageTarget={id ? 'Editar Permissão' : 'Nova Permissão'}>
                <Box sx={{ p: 3 }}>
                    <Skeleton variant="rectangular" height={400} />
                </Box>
            </PageScaffold>
        );
    }

    return (
        <PageScaffold
            links={[]}
            titlePageTarget={id ? 'Editar Permissão' : 'Nova Permissão'}
            openSnackbar={openSnackbar}
            setOpenSnackbar={setOpenSnackbar}
            message={message}
            severity={severity}
        >
            <Box sx={{ p: 3 }}>
                {/* Botão Voltar */}
                <Box sx={{ mb: 3 }}>
                    <Button
                        startIcon={<ArrowBackIcon />}
                        onClick={() => navigate('/admin/permissions')}
                        variant="outlined"
                        sx={{
                            borderRadius: 2,
                            textTransform: 'none'
                        }}
                    >
                        Voltar
                    </Button>
                </Box>

                <form onSubmit={formik.handleSubmit}>
                    <Grid container spacing={3}>
                        {/* Formulário Principal */}
                        <Grid item xs={12} md={8}>
                            <Card
                                sx={{
                                    borderRadius: 3,
                                    boxShadow: theme.palette.mode === 'dark'
                                        ? '0 4px 20px rgba(0,0,0,0.3)'
                                        : '0 4px 20px rgba(0,0,0,0.08)',
                                }}
                            >
                                <CardContent sx={{ p: 3 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 1 }}>
                                        <SecurityIcon sx={{ color: 'primary.main', fontSize: 28 }} />
                                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                            Informações da Permissão
                                        </Typography>
                                    </Box>

                                    <Stack spacing={3}>
                                        <TextField
                                            fullWidth
                                            id="name"
                                            name="name"
                                            label="Nome"
                                            value={formik.values.name}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            error={formik.touched.name && Boolean(formik.errors.name)}
                                            helperText={formik.touched.name && formik.errors.name}
                                            disabled={loading}
                                            sx={standaloneFieldSx}
                                        />

                                        <TextField
                                            fullWidth
                                            id="display_name"
                                            name="display_name"
                                            label="Nome de Exibição"
                                            value={formik.values.display_name}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            error={formik.touched.display_name && Boolean(formik.errors.display_name)}
                                            helperText={formik.touched.display_name && formik.errors.display_name}
                                            disabled={loading}
                                            sx={standaloneFieldSx}
                                        />

                                        <TextField
                                            fullWidth
                                            id="group_permission"
                                            name="group_permission"
                                            label="Grupo de Permissão"
                                            value={formik.values.group_permission}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            error={formik.touched.group_permission && Boolean(formik.errors.group_permission)}
                                            helperText={formik.touched.group_permission && formik.errors.group_permission}
                                            disabled={loading}
                                            sx={standaloneFieldSx}
                                        />
                                    </Stack>
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Path Accesses */}
                        {id && (
                            <Grid item xs={12} md={4}>
                                <Card
                                    sx={{
                                        borderRadius: 3,
                                        boxShadow: theme.palette.mode === 'dark'
                                            ? '0 4px 20px rgba(0,0,0,0.3)'
                                            : '0 4px 20px rgba(0,0,0,0.08)',
                                    }}
                                >
                                    <CardContent sx={{ p: 3 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <RouteIcon sx={{ color: 'primary.main', fontSize: 24 }} />
                                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                                    Path Accesses
                                                </Typography>
                                            </Box>
                                            <Tooltip title="Adicionar Path Access">
                                                <IconButton
                                                    color="primary"
                                                    onClick={() => handleOpenPathAccessDialog()}
                                                    size="small"
                                                >
                                                    <AddIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>

                                        {loadingPathAccesses ? (
                                            <Skeleton variant="rectangular" height={200} />
                                        ) : pathAccesses.length === 0 ? (
                                            <Alert severity="info" sx={{ mt: 2 }}>
                                                Nenhum path access cadastrado
                                            </Alert>
                                        ) : (
                                            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
                                                <Table size="small" stickyHeader>
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell>Path</TableCell>
                                                            <TableCell>Method</TableCell>
                                                            <TableCell align="right">Ações</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {pathAccesses.map((pathAccess) => (
                                                            <TableRow key={pathAccess.id}>
                                                                <TableCell sx={{ wordBreak: 'break-word' }}>
                                                                    {pathAccess.path}
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Chip
                                                                        label={pathAccess.method}
                                                                        size="small"
                                                                        color={
                                                                            pathAccess.method === 'GET' ? 'primary' :
                                                                            pathAccess.method === 'POST' ? 'success' :
                                                                            pathAccess.method === 'PUT' ? 'warning' :
                                                                            pathAccess.method === 'PATCH' ? 'info' :
                                                                            'error'
                                                                        }
                                                                    />
                                                                </TableCell>
                                                                <TableCell align="right">
                                                                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                                                                        <Tooltip title="Editar">
                                                                            <IconButton
                                                                                size="small"
                                                                                onClick={() => handleOpenPathAccessDialog(pathAccess)}
                                                                            >
                                                                                <EditIcon fontSize="small" />
                                                                            </IconButton>
                                                                        </Tooltip>
                                                                        <Tooltip title="Excluir">
                                                                            <IconButton
                                                                                size="small"
                                                                                color="error"
                                                                                onClick={() => handleDeletePathAccess(pathAccess.id)}
                                                                            >
                                                                                <DeleteIcon fontSize="small" />
                                                                            </IconButton>
                                                                        </Tooltip>
                                                                    </Stack>
                                                                </TableCell>
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        )}
                                    </CardContent>
                                </Card>
                            </Grid>
                        )}

                        {/* Botões de Ação */}
                        <Grid item xs={12}>
                            <Stack direction="row" spacing={2} justifyContent="flex-end">
                                <Button
                                    variant="outlined"
                                    startIcon={<CancelIcon />}
                                    onClick={() => navigate('/admin/permissions')}
                                    disabled={loading}
                                    sx={{ borderRadius: 2, textTransform: 'none' }}
                                >
                                    Cancelar
                                </Button>
                                {id && (
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        startIcon={<SaveIcon />}
                                        disabled={loading}
                                        sx={{ borderRadius: 2, textTransform: 'none' }}
                                    >
                                        {loading ? 'Salvando...' : 'Salvar'}
                                    </Button>
                                )}
                            </Stack>
                        </Grid>
                    </Grid>
                </form>

                {/* Dialog para adicionar/editar Path Access */}
                <Dialog
                    open={openPathAccessDialog}
                    onClose={handleClosePathAccessDialog}
                    maxWidth="sm"
                    fullWidth
                    PaperProps={{
                        sx: {
                            borderRadius: 3
                        }
                    }}
                >
                    <DialogTitle>
                        {editingPathAccess ? 'Editar Path Access' : 'Adicionar Path Access'}
                    </DialogTitle>
                    <DialogContent>
                        <Stack spacing={3} sx={{ mt: 1 }}>
                            <TextField
                                fullWidth
                                label="Path"
                                value={pathAccessForm.path}
                                onChange={(e) => setPathAccessForm({ ...pathAccessForm, path: e.target.value })}
                                placeholder="/admin/example"
                                required
                                sx={standaloneFieldSx}
                            />
                            {/* Select de rótulo estático (`.ffs`) — não flutua */}
                            <Dropdown
                                label="Método HTTP"
                                labelVariant="static-micro"
                                data={HTTP_METHODS.map((method) => ({ id: method, name: method }))}
                                selectedItem={pathAccessForm.method}
                                handleChange={(value) => setPathAccessForm({ ...pathAccessForm, method: value })}
                                variant="outlined"
                                size="medium"
                                sx={standaloneFieldSx}
                            />
                        </Stack>
                    </DialogContent>
                    <DialogActions sx={{ p: 2 }}>
                        <Button onClick={handleClosePathAccessDialog} sx={{ textTransform: 'none' }}>
                            Cancelar
                        </Button>
                        <Button
                            onClick={handleSavePathAccess}
                            variant="contained"
                            disabled={loading || !pathAccessForm.path || !pathAccessForm.method}
                            sx={{ textTransform: 'none' }}
                        >
                            {loading ? 'Salvando...' : 'Salvar'}
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </PageScaffold>
    );
}

